import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { Button } from "@warp-ds/react";
import { Alert } from "@warp-ds/react/components/alert";
import { Expandable } from "@warp-ds/react/components/expandable";
import { useEffect, useState } from "react";
import { type GoogleMapsEmbed, useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "../../../utils/pulse-tracking/usePulse.ts";

const GoogleMaps = ({ mapsEmbed, address }: { mapsEmbed: GoogleMapsEmbed; address: string }) => {
  const { isMobile, locale, consent } = useAppConfig();
  const [hasConsent, setHasConsent] = useState<boolean>(consent.marketing);

  return (
    <>
      {hasConsent ? (
        <iframe
          className="border rounded-8 mt-16"
          loading="lazy"
          title="Google Maps"
          width="100%"
          height={isMobile ? "220" : "310"}
          referrerPolicy="no-referrer-when-downgrade"
          src={`${mapsEmbed.apiUrl}?key=${mapsEmbed.apiKey}&zoom=10&language=${locale}&q=${encodeURIComponent(address)}`}
          allowFullScreen
        ></iframe>
      ) : (
        <MissingConsent setConsent={setHasConsent} />
      )}
    </>
  );
};

const MissingConsent = ({ setConsent }: { setConsent: (value: boolean) => void }) => {
  const { trackClick } = usePulse();
  const [hasTracked, setHasTracked] = useState(false);
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleExpand = () => {
    if (!hasTracked) {
      trackClick({
        name: "View map",
        objectId: "CE_ev90",
        elementType: ElementType.Button,
        intent: Intent.Open,
      });
      setHasTracked(true);
      if (getCmpMarketingCookieConsent()) {
        setConsent(true);
      }
    }
  };
  return (
    <Expandable
      title={i18n.t({ id: "dealer.dealerMap.showMap", message: "Show map" })}
      buttonClass="mt-16"
      onChange={toggleExpand}
    >
      <Alert type="info" show className="mt-16">
        <div className="flex flex-wrap gap-8 text-m">
          <Trans
            id="dealer.dealerMap.needConsentText"
            message="To view the map, you must first accept marketing cookies. You may need to reload the page afterwards."
          />
          {isClient && window._tcf_ && window._tcf_.showPrivacyManager && (
            <Button
              link
              onClick={() => {
                trackClick({
                  name: "Cookie settings",
                  objectId: "CE_ev91",
                  elementType: ElementType.Link,
                  intent: Intent.Edit,
                });
                if (window._tcf_?.showPrivacyManager) {
                  window._tcf_.showPrivacyManager();
                }
              }}
            >
              <Trans id="dealer.dealerMap.openCookiesettings" message="Open cookiesettings" />
            </Button>
          )}
        </div>
      </Alert>
    </Expandable>
  );
};

const getCmpMarketingCookieConsent = (): boolean => {
  if (typeof document === "undefined") return false;

  const cookies = document.cookie.split("; ");
  const cmpCookie = cookies.find((cookie) => cookie.startsWith("_cmp_marketing="));
  const value = cmpCookie?.split("=")[1];

  return value === "1";
};

export default GoogleMaps;
